/**
 * HR & Payroll Management - Full Featured Page
 * Tabs: Dashboard, Staff Directory, Salary Structure, Payroll, Leave Management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (url, token) => {
    try { const r = await fetch(`${API_URL}${url}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }); return await r.json(); } catch (e) { return { error: e.message }; }
  },
  post: async (url, data, token) => {
    try { const r = await fetch(`${API_URL}${url}`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(data) }); return await r.json(); } catch (e) { return { error: e.message }; }
  },
  put: async (url, token) => {
    try { const r = await fetch(`${API_URL}${url}`, { method: 'PUT', headers: token ? { Authorization: `Bearer ${token}` } : {} }); return await r.json(); } catch (e) { return { error: e.message }; }
  },
};

function Toast({ toast }) {
  if (!toast) return null;
  return <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>{toast.message}</div>;
}

const DESG_LABELS = { teacher: 'Teacher', senior_teacher: 'Sr. Teacher', hod: 'HOD', vice_principal: 'Vice Principal', principal: 'Principal', clerk: 'Clerk', accountant: 'Accountant', peon: 'Peon', guard: 'Guard', driver: 'Driver', librarian: 'Librarian', lab_assistant: 'Lab Asst.', counselor: 'Counselor', other: 'Other' };
const DEPT_LABELS = { academic: 'Academic', administration: 'Admin', accounts: 'Accounts', sports: 'Sports', library: 'Library', transport: 'Transport', maintenance: 'Maintenance', it: 'IT', other: 'Other' };

// ==================== DASHBOARD TAB ====================
function DashboardTab({ token, language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/hr/dashboard', token);
      if (res.success) setData(res);
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!data) return <div className="p-8 text-center text-gray-500">No data</div>;

  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="hr-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="rounded-xl border p-4 bg-blue-50 border-blue-200 text-blue-700">
          <div className="text-sm opacity-80">{language === 'hi' ? 'कुल स्टाफ' : 'Total Staff'}</div>
          <div className="text-2xl font-bold mt-1">{s.total_staff}</div>
        </div>
        <div className="rounded-xl border p-4 bg-green-50 border-green-200 text-green-700">
          <div className="text-sm opacity-80">{language === 'hi' ? 'मासिक वेतन' : 'Monthly Salary'}</div>
          <div className="text-2xl font-bold mt-1">{s.total_monthly_salary > 0 ? `₹${(s.total_monthly_salary/1000).toFixed(0)}K` : '₹0'}</div>
        </div>
        <div className="rounded-xl border p-4 bg-yellow-50 border-yellow-200 text-yellow-700">
          <div className="text-sm opacity-80">{language === 'hi' ? 'लंबित छुट्टी' : 'Pending Leaves'}</div>
          <div className="text-2xl font-bold mt-1">{s.pending_leaves}</div>
        </div>
        <div className="rounded-xl border p-4 bg-orange-50 border-orange-200 text-orange-700">
          <div className="text-sm opacity-80">{language === 'hi' ? 'इस महीने पेरोल' : 'Payroll Status'}</div>
          <div className="text-2xl font-bold mt-1">{s.payroll_done_this_month ? '✅' : '❌'}</div>
          <div className="text-xs opacity-70">{s.current_month}</div>
        </div>
        <div className="rounded-xl border p-4 bg-purple-50 border-purple-200 text-purple-700">
          <div className="text-sm opacity-80">{language === 'hi' ? 'अंतिम पेरोल' : 'Last Payroll'}</div>
          <div className="text-2xl font-bold mt-1">{s.last_payroll_month || '-'}</div>
        </div>
      </div>

      {Object.keys(data.department_breakdown).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'विभाग-वार' : 'By Department'}</h3>
            {Object.entries(data.department_breakdown).map(([dept, count]) => (
              <div key={dept} className="flex items-center justify-between py-1.5 border-b last:border-0">
                <span className="text-sm text-gray-600">{DEPT_LABELS[dept] || dept}</span>
                <span className="text-sm font-bold text-gray-800">{count}</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'पद-वार' : 'By Designation'}</h3>
            {Object.entries(data.designation_breakdown).map(([desig, count]) => (
              <div key={desig} className="flex items-center justify-between py-1.5 border-b last:border-0">
                <span className="text-sm text-gray-600">{DESG_LABELS[desig] || desig}</span>
                <span className="text-sm font-bold text-gray-800">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {s.total_staff === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border text-gray-400">
          <div className="text-5xl mb-3">👨‍💼</div>
          <p className="text-lg">{language === 'hi' ? 'अभी कोई स्टाफ नहीं जोड़ा' : 'No staff added yet'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== STAFF DIRECTORY TAB ====================
function StaffDirectoryTab({ token, language }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', employee_id: '', designation: 'teacher', department: 'academic', phone: '', basic_salary: 0, joining_date: '', qualification: '', gender: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get('/api/hr/staff', token);
    setStaff(res.staff || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.name) return;
    setSaving(true);
    const res = await api.post('/api/hr/staff', form, token);
    setSaving(false);
    if (res.success) {
      setShowAdd(false);
      setForm({ name: '', employee_id: '', designation: 'teacher', department: 'academic', phone: '', basic_salary: 0, joining_date: '', qualification: '', gender: '' });
      load();
      setToast({ message: language === 'hi' ? 'स्टाफ जोड़ा गया!' : 'Staff added!', type: 'success' });
    } else {
      setToast({ message: res.detail || 'Error', type: 'error' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  const desgColors = { teacher: 'bg-blue-100 text-blue-700', principal: 'bg-purple-100 text-purple-700', hod: 'bg-green-100 text-green-700', clerk: 'bg-gray-100 text-gray-700' };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="staff-directory-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{staff.length} {language === 'hi' ? 'कर्मचारी' : 'staff members'}</p>
        <button data-testid="add-staff-btn" onClick={() => setShowAdd(!showAdd)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          {showAdd ? '✕' : '+'} {language === 'hi' ? 'स्टाफ जोड़ें' : 'Add Staff'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="add-staff-form">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'नया कर्मचारी' : 'New Staff Member'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input data-testid="staff-name" placeholder={language === 'hi' ? 'नाम *' : 'Full Name *'}
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={language === 'hi' ? 'कर्मचारी ID' : 'Employee ID'}
              value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <select data-testid="staff-designation" value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              {Object.entries(DESG_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              {Object.entries(DEPT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input placeholder={language === 'hi' ? 'फ़ोन' : 'Phone'} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <input type="number" placeholder={language === 'hi' ? 'मासिक वेतन (₹)' : 'Monthly Salary (₹)'}
              value={form.basic_salary || ''} onChange={e => setForm({ ...form, basic_salary: parseFloat(e.target.value) || 0 })} className="px-3 py-2 border rounded-lg text-sm" />
            <input type="date" placeholder="Joining Date" value={form.joining_date} onChange={e => setForm({ ...form, joining_date: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={language === 'hi' ? 'योग्यता' : 'Qualification'} value={form.qualification} onChange={e => setForm({ ...form, qualification: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">{language === 'hi' ? 'लिंग' : 'Gender'}</option>
              <option value="male">{language === 'hi' ? 'पुरुष' : 'Male'}</option>
              <option value="female">{language === 'hi' ? 'महिला' : 'Female'}</option>
            </select>
          </div>
          <button data-testid="save-staff-btn" onClick={handleAdd} disabled={saving || !form.name}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {saving ? '...' : (language === 'hi' ? 'स्टाफ जोड़ें' : 'Add Staff')}
          </button>
        </div>
      )}

      {/* Staff Table */}
      {staff.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="staff-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'कर्मचारी' : 'Employee'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'पद' : 'Designation'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'विभाग' : 'Dept'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'वेतन' : 'Salary'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'फ़ोन' : 'Phone'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {staff.map(s => (
                  <tr key={s.staff_id} className="hover:bg-gray-50" data-testid={`staff-row-${s.staff_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">{s.name?.charAt(0)}</div>
                        <div>
                          <div className="text-sm font-medium text-gray-800">{s.name}</div>
                          {s.qualification && <div className="text-[10px] text-gray-400">{s.qualification}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono text-gray-500">{s.employee_id}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${desgColors[s.designation] || 'bg-gray-100 text-gray-600'}`}>
                        {DESG_LABELS[s.designation] || s.designation}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">{DEPT_LABELS[s.department] || s.department}</td>
                    <td className="px-4 py-2.5 text-center text-sm font-bold text-green-600">₹{(s.basic_salary || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">{s.phone || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {staff.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">👨‍💼</div>
          <p>{language === 'hi' ? 'कोई स्टाफ नहीं' : 'No staff members'}</p>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}

// ==================== SALARY STRUCTURE TAB ====================
function SalaryStructureTab({ token, language }) {
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/hr/salary-structure', token);
      setStructure(res.structures?.[0] || null);
      setLoading(false);
    })();
  }, [token]);

  const handleSave = async () => {
    if (!structure) return;
    setSaving(true);
    const res = await api.post('/api/hr/salary-structure', structure, token);
    setSaving(false);
    setToast({ message: res.success ? (language === 'hi' ? 'सेव हो गया!' : 'Saved!') : 'Error', type: res.success ? 'success' : 'error' });
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!structure) return <div className="p-8 text-center text-gray-500">No structure</div>;

  const Field = ({ label, field, suffix = '%' }) => (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input type="number" value={structure[field] || 0}
          onChange={e => setStructure({ ...structure, [field]: parseFloat(e.target.value) || 0 })}
          className="w-full px-3 py-2 border rounded-lg text-sm" data-testid={`salary-${field}`} />
        <span className="text-xs text-gray-400 w-6">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="salary-structure-tab">
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-gray-800 mb-4">{language === 'hi' ? 'वेतन संरचना (Gross Salary का %)' : 'Salary Structure (% of Gross)'}</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-semibold text-green-600 mb-3 uppercase tracking-wider">{language === 'hi' ? 'कमाई (Earnings)' : 'Earnings'}</h4>
            <div className="space-y-3">
              <Field label={language === 'hi' ? 'बेसिक (Basic)' : 'Basic'} field="basic_percentage" />
              <Field label="HRA" field="hra_percentage" />
              <Field label={language === 'hi' ? 'DA (महंगाई भत्ता)' : 'DA (Dearness)'} field="da_percentage" />
              <Field label={language === 'hi' ? 'TA (यात्रा भत्ता)' : 'TA (Travel)'} field="ta_percentage" />
              <Field label={language === 'hi' ? 'मेडिकल भत्ता' : 'Medical Allowance'} field="medical_allowance" suffix="₹" />
              <Field label={language === 'hi' ? 'अन्य भत्ता' : 'Other Allowance'} field="other_allowance" suffix="₹" />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-red-600 mb-3 uppercase tracking-wider">{language === 'hi' ? 'कटौती (Deductions)' : 'Deductions'}</h4>
            <div className="space-y-3">
              <Field label="PF (Provident Fund)" field="pf_percentage" />
              <Field label="ESI" field="esi_percentage" />
              <Field label={language === 'hi' ? 'प्रोफेशनल टैक्स' : 'Professional Tax'} field="professional_tax" suffix="₹" />
              <Field label="TDS" field="tds_percentage" />
            </div>
          </div>
        </div>

        <button data-testid="save-salary-structure" onClick={handleSave} disabled={saving}
          className="w-full mt-6 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
          {saving ? '...' : (language === 'hi' ? 'संरचना सेव करें' : 'Save Structure')}
        </button>
      </div>

      {/* Example Calculation */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'उदाहरण: ₹30,000 वेतन' : 'Example: ₹30,000 Salary'}</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-medium text-green-600 mb-2">{language === 'hi' ? 'कमाई' : 'Earnings'}</div>
            <div className="space-y-1">
              <div className="flex justify-between"><span>Basic</span><span>₹{(30000 * structure.basic_percentage / 100).toFixed(0)}</span></div>
              <div className="flex justify-between"><span>HRA</span><span>₹{(30000 * structure.hra_percentage / 100).toFixed(0)}</span></div>
              <div className="flex justify-between"><span>DA</span><span>₹{(30000 * structure.da_percentage / 100).toFixed(0)}</span></div>
              <div className="flex justify-between"><span>TA</span><span>₹{(30000 * structure.ta_percentage / 100).toFixed(0)}</span></div>
              <div className="flex justify-between"><span>Medical</span><span>₹{structure.medical_allowance}</span></div>
              <div className="flex justify-between font-bold border-t pt-1">
                <span>Total</span>
                <span>₹{(30000 * (structure.basic_percentage + structure.hra_percentage + structure.da_percentage + structure.ta_percentage) / 100 + structure.medical_allowance + structure.other_allowance).toFixed(0)}</span>
              </div>
            </div>
          </div>
          <div>
            <div className="font-medium text-red-600 mb-2">{language === 'hi' ? 'कटौती' : 'Deductions'}</div>
            <div className="space-y-1">
              <div className="flex justify-between"><span>PF</span><span>₹{(30000 * structure.basic_percentage / 100 * structure.pf_percentage / 100).toFixed(0)}</span></div>
              <div className="flex justify-between"><span>ESI</span><span>₹{(30000 * structure.esi_percentage / 100).toFixed(0)}</span></div>
              <div className="flex justify-between"><span>PT</span><span>₹{structure.professional_tax}</span></div>
              <div className="flex justify-between"><span>TDS</span><span>₹{(30000 * structure.tds_percentage / 100).toFixed(0)}</span></div>
            </div>
          </div>
        </div>
      </div>
      <Toast toast={toast} />
    </div>
  );
}

// ==================== PAYROLL TAB ====================
function PayrollTab({ token, language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState(null);
  const [viewPayslip, setViewPayslip] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get('/api/hr/payroll', token);
    setData(res);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const runPayroll = async () => {
    setRunning(true);
    const res = await api.post(`/api/hr/payroll/run?month=${month}`, {}, token);
    setRunning(false);
    if (res.success) {
      setToast({ message: language === 'hi' ? `${res.staff_processed} स्टाफ का पेरोल प्रोसेस हुआ! कुल: ₹${res.total_expense.toLocaleString()}` : `Payroll processed for ${res.staff_processed} staff! Total: ₹${res.total_expense.toLocaleString()}`, type: 'success' });
      load();
    } else {
      setToast({ message: res.detail || 'Error', type: 'error' });
    }
    setTimeout(() => setToast(null), 5000);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="payroll-tab">
      {/* Run Payroll */}
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm" data-testid="payroll-month" />
        <button data-testid="run-payroll-btn" onClick={runPayroll} disabled={running}
          className="px-6 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition">
          {running ? '...' : (language === 'hi' ? 'पेरोल चलाएं' : 'Run Payroll')}
        </button>
        <span className="text-xs text-gray-400 ml-auto">{language === 'hi' ? 'सभी active स्टाफ का salary calculate होगा' : 'Will calculate salary for all active staff'}</span>
      </div>

      {/* Payslip Modal */}
      {viewPayslip && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setViewPayslip(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="payslip-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-800">{language === 'hi' ? 'पे-स्लिप' : 'Payslip'}</h3>
              <button onClick={() => setViewPayslip(null)} className="text-gray-400">✕</button>
            </div>
            <div className="bg-slate-900 text-white rounded-lg p-4 mb-4 text-center">
              <div className="font-bold text-lg">{viewPayslip.name}</div>
              <div className="text-xs text-slate-300">{DESG_LABELS[viewPayslip.designation] || viewPayslip.designation} | {viewPayslip.employee_id} | {viewPayslip.month}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-semibold text-green-600 mb-2">{language === 'hi' ? 'कमाई' : 'Earnings'}</div>
                {Object.entries(viewPayslip.earnings || {}).filter(([k]) => k !== 'total').map(([k, v]) => (
                  <div key={k} className="flex justify-between py-0.5"><span className="capitalize">{k}</span><span>₹{v.toLocaleString()}</span></div>
                ))}
                <div className="flex justify-between font-bold border-t mt-1 pt-1 text-green-700">
                  <span>Total</span><span>₹{viewPayslip.earnings?.total?.toLocaleString()}</span>
                </div>
              </div>
              <div>
                <div className="font-semibold text-red-600 mb-2">{language === 'hi' ? 'कटौती' : 'Deductions'}</div>
                {Object.entries(viewPayslip.deductions || {}).filter(([k]) => k !== 'total').map(([k, v]) => (
                  <div key={k} className="flex justify-between py-0.5"><span className="capitalize">{k.replace('_', ' ')}</span><span>₹{v.toLocaleString()}</span></div>
                ))}
                <div className="flex justify-between font-bold border-t mt-1 pt-1 text-red-700">
                  <span>Total</span><span>₹{viewPayslip.deductions?.total?.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
              <div className="text-xs text-orange-500">{language === 'hi' ? 'शुद्ध वेतन' : 'Net Salary'}</div>
              <div className="text-2xl font-black text-orange-600">₹{viewPayslip.net_salary?.toLocaleString()}</div>
            </div>
            <div className="flex gap-3 mt-4 print:hidden">
              <button onClick={() => window.print()} className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition" data-testid="payslip-print-btn">
                {language === 'hi' ? '🖨️ प्रिंट करें' : '🖨️ Print'}
              </button>
              <button
                data-testid="payslip-whatsapp-share-btn"
                onClick={() => {
                  const msg = `${language === 'hi' ? '📄 वेतन पर्ची' : '📄 Payslip'} - ${viewPayslip.month}%0A` +
                    `${language === 'hi' ? 'कर्मचारी' : 'Employee'}: ${viewPayslip.name} (${viewPayslip.employee_id})%0A` +
                    `${language === 'hi' ? 'विभाग' : 'Dept'}: ${viewPayslip.department || '-'}%0A` +
                    `${language === 'hi' ? 'कुल कमाई' : 'Gross'}: ₹${viewPayslip.earnings?.total?.toLocaleString()}%0A` +
                    `${language === 'hi' ? 'कुल कटौती' : 'Deductions'}: ₹${viewPayslip.deductions?.total?.toLocaleString()}%0A` +
                    `${language === 'hi' ? 'शुद्ध वेतन' : 'Net Salary'}: ₹${viewPayslip.net_salary?.toLocaleString()}%0A%0A` +
                    `- शिक्षा मित्र`;
                  window.open(`https://wa.me/?text=${msg}`, '_blank');
                }}
                className="flex-1 py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition"
              >
                {language === 'hi' ? '📱 WhatsApp पर भेजें' : '📱 Share on WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Payroll History */}
      {data?.months?.length > 0 ? (
        data.months.map(m => (
          <div key={m.month} className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800">{m.month}</h3>
                <p className="text-xs text-gray-500">{m.staff_count} {language === 'hi' ? 'कर्मचारी' : 'staff'} | ₹{m.total_expense.toLocaleString()}</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'कर्मचारी' : 'Employee'}</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-green-600">{language === 'hi' ? 'कमाई' : 'Earnings'}</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-red-600">{language === 'hi' ? 'कटौती' : 'Deductions'}</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-orange-600">{language === 'hi' ? 'शुद्ध वेतन' : 'Net'}</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {m.payslips.map(p => (
                    <tr key={p.payslip_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm"><span className="font-medium">{p.name}</span> <span className="text-xs text-gray-400">{p.employee_id}</span></td>
                      <td className="px-4 py-2 text-center text-sm text-green-600">₹{p.earnings?.total?.toLocaleString()}</td>
                      <td className="px-4 py-2 text-center text-sm text-red-600">₹{p.deductions?.total?.toLocaleString()}</td>
                      <td className="px-4 py-2 text-center text-sm font-bold text-orange-600">₹{p.net_salary?.toLocaleString()}</td>
                      <td className="px-4 py-2 text-center">
                        <button onClick={() => setViewPayslip(p)} className="px-3 py-1 bg-orange-50 text-orange-600 rounded text-xs font-medium hover:bg-orange-100" data-testid={`view-payslip-${p.payslip_id}`}>
                          {language === 'hi' ? 'देखें' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">💰</div>
          <p>{language === 'hi' ? 'अभी कोई पेरोल नहीं' : 'No payroll processed yet'}</p>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}

// ==================== LEAVE MANAGEMENT TAB ====================
function LeaveTab({ token, language }) {
  const [leaves, setLeaves] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ staff_id: '', leave_type: 'CL', start_date: '', end_date: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const [lRes, sRes] = await Promise.all([
      api.get('/api/hr/leaves', token),
      api.get('/api/hr/staff', token),
    ]);
    setLeaves(lRes.leaves || []);
    setStaff(sRes.staff || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleApply = async () => {
    if (!form.staff_id || !form.start_date) return;
    setSaving(true);
    const res = await api.post('/api/hr/leaves', form, token);
    setSaving(false);
    if (res.success) {
      setShowAdd(false);
      setForm({ staff_id: '', leave_type: 'CL', start_date: '', end_date: '', reason: '' });
      load();
      setToast({ message: language === 'hi' ? 'छुट्टी आवेदन भेजा गया!' : 'Leave applied!', type: 'success' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  const handleAction = async (leaveId, action) => {
    const res = await api.put(`/api/hr/leaves/${leaveId}/${action}`, token);
    if (res.success) { load(); setToast({ message: res.message, type: 'success' }); }
    setTimeout(() => setToast(null), 3000);
  };

  const statusColors = { pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };
  const leaveTypeLabels = { CL: 'Casual', EL: 'Earned', SL: 'Sick', ML: 'Maternity', PL: 'Paternity' };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="leave-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{leaves.length} {language === 'hi' ? 'आवेदन' : 'applications'}</p>
        <button data-testid="apply-leave-btn" onClick={() => setShowAdd(!showAdd)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          {showAdd ? '✕' : '+'} {language === 'hi' ? 'छुट्टी आवेदन' : 'Apply Leave'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="apply-leave-form">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'छुट्टी आवेदन' : 'Apply Leave'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select data-testid="leave-staff" value={form.staff_id} onChange={e => setForm({ ...form, staff_id: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">{language === 'hi' ? 'कर्मचारी चुनें *' : 'Select Staff *'}</option>
              {staff.map(s => <option key={s.staff_id} value={s.staff_id}>{s.name} ({s.employee_id})</option>)}
            </select>
            <select value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              {Object.entries(leaveTypeLabels).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
            </select>
            <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" placeholder="Start Date" />
            <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" placeholder="End Date" />
          </div>
          <input placeholder={language === 'hi' ? 'कारण' : 'Reason'} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" />
          <button data-testid="save-leave-btn" onClick={handleApply} disabled={saving || !form.staff_id || !form.start_date}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {saving ? '...' : (language === 'hi' ? 'आवेदन भेजें' : 'Submit')}
          </button>
        </div>
      )}

      {/* Leave List */}
      {leaves.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leave-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'कर्मचारी' : 'Staff'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'प्रकार' : 'Type'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'तारीख' : 'Dates'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'कारण' : 'Reason'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'स्थिति' : 'Status'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'कार्रवाई' : 'Action'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {leaves.map(l => (
                  <tr key={l.leave_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-sm font-medium">{l.staff_name || l.staff_id?.slice(0, 8)}</td>
                    <td className="px-4 py-2.5 text-center"><span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">{l.leave_type}</span></td>
                    <td className="px-4 py-2.5 text-center text-xs text-gray-500">{l.start_date} → {l.end_date || l.start_date}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-500 max-w-[200px] truncate">{l.reason || '-'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${statusColors[l.status]}`}>{l.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {l.status === 'pending' && (
                        <div className="flex gap-1 justify-center">
                          <button data-testid={`approve-${l.leave_id}`} onClick={() => handleAction(l.leave_id, 'approve')} className="px-2 py-1 bg-green-50 text-green-600 rounded text-xs hover:bg-green-100">✅</button>
                          <button data-testid={`reject-${l.leave_id}`} onClick={() => handleAction(l.leave_id, 'reject')} className="px-2 py-1 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100">❌</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {leaves.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">📋</div>
          <p>{language === 'hi' ? 'कोई छुट्टी आवेदन नहीं' : 'No leave applications'}</p>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function HRPayrollPage({ token }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', label: language === 'hi' ? 'डैशबोर्ड' : 'Dashboard', icon: '📊' },
    { id: 'staff', label: language === 'hi' ? 'स्टाफ' : 'Staff', icon: '👨‍💼' },
    { id: 'salary', label: language === 'hi' ? 'वेतन संरचना' : 'Salary', icon: '💰' },
    { id: 'payroll', label: language === 'hi' ? 'पेरोल' : 'Payroll', icon: '📋' },
    { id: 'leave', label: language === 'hi' ? 'छुट्टी' : 'Leave', icon: '🏖️' },
  ];

  return (
    <div className="space-y-4" data-testid="hr-payroll-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{language === 'hi' ? 'HR और पेरोल' : 'HR & Payroll'}</h1>
        <p className="text-sm text-gray-500">{language === 'hi' ? 'कर्मचारी प्रबंधन, वेतन और छुट्टी' : 'Staff management, salary & leave'}</p>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit flex-wrap" data-testid="hr-tabs">
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === tab.id ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
            <span className="mr-1.5">{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <DashboardTab token={token} language={language} />}
      {activeTab === 'staff' && <StaffDirectoryTab token={token} language={language} />}
      {activeTab === 'salary' && <SalaryStructureTab token={token} language={language} />}
      {activeTab === 'payroll' && <PayrollTab token={token} language={language} />}
      {activeTab === 'leave' && <LeaveTab token={token} language={language} />}
    </div>
  );
}
